<template>
  <div class="account-owner box">
    <div style="float: right">
      <number-chip
        :value="pendingUsersCount"
        :label="$t('common.pending_invites')"
        @click.native="modals.showPendingUsersModal = true"
        v-show="hasPendingUsers"
      />
    </div>
    <el-table :data="admins" style="width: 100%">
      <el-table-column
        prop="email"
        :label="$t('organization_profile.admins')"
        width="220"
      >
        <template slot-scope="scope">
          <div class="owner-info">
            <p>{{ scope.row.first_name + " " + scope.row.last_name }}</p>
            <p>{{ scope.row.email }}</p>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="role"
        :label="$t('organizations.table.roles')"
        width="220"
      >
        <template slot-scope="scope">
          <div class="owner-info">
            <span>{{
              scope.row.roles.map((x) => getRoleDisplayName(x)).join(", ")
            }}</span>
          </div>
        </template>
      </el-table-column>
      <base-table-column
        prop="created"
        dataType="date"
        :label="$t('organization_profile.date_joined')"
      >
      </base-table-column>
      <el-table-column>
        <template slot-scope="scope">
          <el-popover
            placement="bottom-end"
            trigger="click"
            :width="230"
            :visible-arrow="false"
          >
            <div class="actions-list">
              <span class="edit-learner" @click="editLearner(scope.row)" :title="$t('student.edit_learner')">
                {{ $t("common.edit") }}
              </span>
              <span
                class="remove-admin"
                @click="handleRemoveAdmin(scope.row.id, scope.row.first_name)"
              >
                {{
                  $t(
                    "organization_profile.admin_actions.remove_from_organization"
                  )
                }}
              </span>
            </div>
            <el-button slot="reference"><i class="el-icon-more"></i></el-button>
          </el-popover>
        </template>
      </el-table-column>
    </el-table>
    <div
      v-if="isOrganizationAdmin || isElsaAdmin || isOrganizationAssociateAdmin"
      class="invite-owner"
      @click="handleAddAdmin"
    >
      <i class="el-icon-circle-plus-outline" />
      {{ $t("organization_profile.add_associate_admin") }}
    </div>

    <div class="modals">
      <remove-admin
        :dialogVisible="modals.showRemoveAdminModal"
        v-on:closed="modals.showRemoveAdminModal = false"
        :organization="organization"
        :adminRemoveId="adminRemoveId"
        :adminName="adminName"
      />
      <invite-admin
        :dialogVisible="modals.showInviteAdminModal"
        v-on:closed="modals.showInviteAdminModal = false"
        :organizationId="organization.id"
        roleAdmin="associate_admin"
        v-on:update-pending="updatePendingInvite"
      />
      <pending-invite-users
        :title="$t('common.pending_invites')"
        :dialogVisible="modals.showPendingUsersModal"
        @closed="modals.showPendingUsersModal = false"
        :pendingInviteUsers="pendingInviteUsersPage"
        :currentPage.sync="pendingInviteUsersCurrentPage"
        :total="pendingUsersCount"
        pendingInviteTypeScope="organization"
      />
      <edit-organization-learner 
        :dialogVisible="modals.showEditLearnerModal" 
        v-on:closed="modals.showEditLearnerModal = false" 
        :organizationType="currentOrganizationType" 
        @updateLearner="updateLearner"
        :userEdited="userEdited"
        :title="$t('organization_profile.admin_actions.edit_user')"
      />
    </div>
  </div>
</template>

<script>
// import ChipIcon from '@/components/common/ChipIcon';
import RemoveAdmin from "@/components/modals/organizations/RemoveAdmin";
import InviteAdmin from "@/components/modals/organizations/InviteAdmin";
// import InitialAvatar from '@/components/common/InitialAvatar'
import BaseTableColumn from "@/components/classes/BaseTableColumn";
import PendingInviteUsers from "@/components/modals/PendingInviteUsers";
import NumberChip from "@/components/common/NumberChip";
import { mapGetters, mapActions } from "vuex";
import { compose, propEq, any, propOr, lt } from "ramda";
import { ADMIN, ASSOCIATE_ADMIN } from "../../constants/userRoles";
import EditOrganizationLearner from '@/components/modals/organizations/EditOrganizationLearner'
import { warningMessage, successMessage } from '@/utils/messages'
export default {
  components: {
    RemoveAdmin,
    InviteAdmin,
    // InitialAvatar,
    // ChipIcon,
    BaseTableColumn,
    PendingInviteUsers,
    NumberChip,
    EditOrganizationLearner
  },
  props: {
    fromAdmin: {
      type: Boolean,
      default: false,
    },
    organization: {
      type: Object,
      default: () => {},
    },
    organizationId: {},
  },
  data() {
    return {
      modals: {
        showRemoveAdminModal: false,
        showInviteAdminModal: false,
        showPendingUsersModal: false,
        showEditLearnerModal: false,
      },
      organizationUserData: {},
      adminRemoveId: "",
      adminName: "",
      pendingInviteUsersCurrentPage: 1,
      userEdited: {
        id: '',
        first_name: '',
        last_name: '',
        roles: []
      }
    };
  },
  computed: {
    admins: propOr([], "currentOrganizationAdmins"),
    ...mapGetters("user", ["isElsaAdmin"]),
    ...mapGetters("organization", [
      "isOrganizationAdmin",
      "isOrganizationAssociateAdmin",
      "currentOrganizationAdmins",
      "pendingUsersCount",
      "pendingInviteUsersPage",
      "currentOrganizationType",
    ]),
    hasPendingUsers: compose(lt(0), propOr(0, "pendingUsersCount")),
  },
  methods: {
    getRoleDisplayName(role) {
      if (role === "admin") {
        return this.$t('organizations.table.role.default_admin');
      } else if (role === "associate_admin") {
        return this.$t('organizations.table.role.associate_admin');
      } else {
        const tempRole = role.replace("_", " ");
        return this.titleCase(tempRole);
      }
    },
    titleCase(str) {
      var splitStr = str.toLowerCase().split(" ");
      for (var i = 0; i < splitStr.length; i++) {
        // You do not need to check if i is larger than splitStr length, as your for does that for you
        // Assign it back to the array
        splitStr[i] =
          splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
      }
      // Directly return the joined string
      return splitStr.join(" ");
    },
    adminHasPendingInvites(admin) {
      const inviteStatus = compose(
        any(propEq("status", "Pending")),
        propOr([], "pending_invitations")
      )(admin);

      return inviteStatus;
    },
    handleRemoveAdmin(adminRemoveId, adminName) {
      this.modals.showRemoveAdminModal = true;
      this.adminRemoveId = adminRemoveId;
      this.adminName = adminName;
    },
    handleEditAdmin(admin) {
      this.organizationUserData = admin;
      this.modals.showEditUserModal = true;
    },
    handleAddAdmin() {
      this.modals.showInviteAdminModal = true;
    },
    updatePendingInvite() {
      const roles = [ADMIN, ASSOCIATE_ADMIN].join();
      this.getPendingInviteUsers({ roles });
    },
    ...mapActions("organization", ["getPendingInviteUsers", "updateOrganizationUserInfo"]),
    updateLearner(user) {
      this.updateOrganizationUserInfo({ userId: user.id, organizationId: this.organizationId, first_name: user.first_name, last_name: user.last_name, roles: user.roles })
        .then(() => {
          const message = this.$i18nLabelsHelper(this.$store.getters['organization/currentOrganizationType'], 'update_learner_of_organization_success')
          successMessage(this.$t(message))(this)
          this.$store.dispatch("organization/getCurrentOrganizationAdmins");
        })
        .catch((error) => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'update_learner_of_organization_warning')
          return warningMessage(this.$t(message))(this)
        })
    },
    editLearner(learner) {
      this.modals.showEditLearnerModal = true
      this.userEdited = {
        id: learner.id,
        first_name: learner.first_name,
        last_name: learner.last_name,
        roles: learner.roles
      }
    },
  },
  watch: {
    organization: {
      immediate: true,
      deep: true,
      handler() {
        const roles = [ADMIN, ASSOCIATE_ADMIN].join();
        this.getPendingInviteUsers({ roles });
        if (this.fromAdmin) {
          return this.$store.dispatch("organization/setOrganizationAdmins", {
            organizationId: this.organization.id,
          });
        }
        return this.$store.dispatch(
          "organization/getCurrentOrganizationAdmins"
        );
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/_colors.scss";

.invite-owner {
  font-weight: 700;
  font-size: 14px;
  color: $purple;
  text-align: initial;
  cursor: pointer;
  padding-top: 24px;
  i {
    font-size: 20px;
    vertical-align: bottom;
    margin-right: 5px;
  }
}
.owner-info {
  span {
    font-weight: 500;
    font-size: 18px;
    color: $black;
  }
  p {
    font-weight: 500;
    font-size: 18px;
    text-transform: capitalize;
    color: $black;
    margin: 0;
    text-align: initial;
    display: flex;
    align-items: center;
    &:last-of-type {
      font-size: 14px;
      color: $light-blue-2;
      text-transform: none;
      line-height: 34px;
    }
    span {
      font-weight: normal;
      font-size: 11px;
      color: rgba(109, 122, 140, 0.5);
      padding-left: 3px;
      display: flex;
      align-items: center;
      i {
        font-size: 18px;
        vertical-align: text-bottom;
        margin-right: 5px;
      }
    }
  }
}
.actions-list {
  .remove-admin {
    color: #eb5757;
    font-size: 16px;
    line-height: 24px;
  }
  .edit-learner {
    text-transform: capitalize;
  }
  span {
    padding-bottom: 0px !important;
  }
}
.account-owner {
  ::v-deep .el-table--enable-row-transition .el-table__body td {
    vertical-align: top;
  }
}
</style>
